import React, { useState, useEffect } from "react";
import {
  Activity,
  Users,
  Image as ImageIcon,
  Calendar,
  RefreshCw,
  AlertCircle,
  Clock,
  Filter,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import {
  getMinuteRangeMonitoring,
  getTodayMonitoringData,
  generateDataForSpecificDateMonitoring,
  getHistoryMonitoringData,
  getAssets
} from '../api/Monitor.api';

interface MonitoringRecord {
  id: number;
  ftpUserId: string;
  monitoringDate: string;
  monitoringHour: number;
  imageCount: number;
  createdAt: string;
}

interface MonitoringResponse {
  monitoringRecords: MonitoringRecord[];
  totalImages: number;
  distinctFtpUsers: number;
  totalDaysWithImages: number;
  // Campos específicos para el rango de minutos
  totalImagesInMinuteRange?: number;
  distinctFtpUsersInMinuteRange?: number;
  ftpUserIdsInMinuteRange?: string[];
  startMinute?: number;
  endMinute?: number;
}

type TabType = 'today' | 'byDate' | 'history' | 'minutes' | 'assets';

const Monitor = () => {
  const [activeTab, setActiveTab] = useState<TabType>('today');
  const [data, setData] = useState<MonitoringResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados para filtros de minutos
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [hour, setHour] = useState('10');
  const [startMin, setStartMin] = useState('0');
  const [endMin, setEndMin] = useState('59');

  // Estados para tabs
  const [todayData, setTodayData] = useState<any>(null);
  const [byDateData, setByDateData] = useState<any>(null);
  const [historyData, setHistoryData] = useState<any>(null);
  const [assetsData, setAssetsData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedFtpUser, setSelectedFtpUser] = useState<string | null>(null);
  const [ftpUserSearch, setFtpUserSearch] = useState('');
  const [showFtpUserList, setShowFtpUserList] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [tabErrors, setTabErrors] = useState({ today: null, byDate: null, history: null, assets: null } as any);

  // Estados para ordenamiento
  type SortOrder = 'asc' | 'desc' | null;
  const [todaySortOrder, setTodaySortOrder] = useState<SortOrder>(null);
  const [byDateSortOrder, setByDateSortOrder] = useState<SortOrder>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMinuteRangeMonitoring(date, parseInt(hour), parseInt(startMin), parseInt(endMin));
      setData(result as MonitoringResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar los datos");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayData = async () => {
    setTabLoading(true);
    setTabErrors(prev => ({ ...prev, today: null }));
    try {
      const today = await getTodayMonitoringData();
      setTodayData(today);
    } catch (err) {
      console.error('Error fetching today data:', err);
      setTabErrors(prev => ({ ...prev, today: 'Error al cargar datos de hoy' }));
    } finally {
      setTabLoading(false);
    }
  };

  const fetchByDateData = async () => {
    setTabLoading(true);
    setTabErrors(prev => ({ ...prev, byDate: null }));
    try {
      const byDate = await generateDataForSpecificDateMonitoring(selectedDate);
      setByDateData(byDate);
    } catch (err) {
      console.error('Error fetching by date data:', err);
      setTabErrors(prev => ({ ...prev, byDate: 'Error al cargar datos de la fecha' }));
    } finally {
      setTabLoading(false);
    }
  };

  const fetchHistoryData = async () => {
    setTabLoading(true);
    setTabErrors(prev => ({ ...prev, history: null }));
    try {
      const history = await getHistoryMonitoringData();
      setHistoryData(history);
    } catch (err) {
      console.error('Error fetching history data:', err);
      setTabErrors(prev => ({ ...prev, history: 'Error al cargar histórico' }));
    } finally {
      setTabLoading(false);
    }
  };

  const fetchAssetsData = async () => {
    setTabLoading(true);
    setTabErrors(prev => ({ ...prev, assets: null }));
    try {
      const assets = await getAssets();
      setAssetsData(assets);
    } catch (err) {
      console.error('Error fetching assets data:', err);
      setTabErrors(prev => ({ ...prev, assets: 'Error al cargar activos' }));
    } finally {
      setTabLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'today') {
      fetchTodayData();
    } else if (activeTab === 'byDate') {
      fetchByDateData();
    } else if (activeTab === 'history') {
      fetchHistoryData();
    } else if (activeTab === 'assets') {
      fetchAssetsData();
    }
  }, [activeTab, selectedDate]);


  const renderFilters = () => {
    if (activeTab === 'today') {
      return (
        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 mb-8 flex flex-wrap items-end gap-4 animate-in fade-in slide-in-from-top-2">
          <button
            onClick={fetchTodayData}
            disabled={tabLoading}
            className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
          >
            {tabLoading ? "Cargando..." : "Actualizar"}
          </button>
        </div>
      );
    }

    if (activeTab === 'byDate') {
      return (
        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 mb-8 flex flex-wrap items-end gap-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1">Seleccionar Fecha</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-medium outline-none"
            />
          </div>
          <button
            onClick={fetchByDateData}
            disabled={tabLoading}
            className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
          >
            {tabLoading ? "Cargando..." : "Consultar"}
          </button>
        </div>
      );
    }

    if (activeTab === 'history') {
      return (
        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 mb-8 flex flex-wrap items-end gap-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1">Feed ID</label>
            <p className="text-xs text-slate-400 mb-1">En caso que no encuentre la fecha en los históricos, se pueden generar los datos en la pestaña de "Por Fecha", luego ingresar para ver el histórico</p>
            <input
              type="text"
              placeholder="Ingresa el Feed ID..."
              value={ftpUserSearch}
              onChange={(e) => setFtpUserSearch(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-medium outline-none"
            />
          </div>
          <button
            onClick={() => {
              if (ftpUserSearch) {
                setSelectedFtpUser(ftpUserSearch);
              }
            }}
            disabled={tabLoading || !ftpUserSearch}
            className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
          >
            {tabLoading ? "Cargando..." : "Ver Datos"}
          </button>
        </div>
      );
    }

    if (activeTab === 'assets') {
      return (
        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 mb-8 flex flex-wrap items-end gap-4 animate-in fade-in slide-in-from-top-2">
          <button
            onClick={fetchAssetsData}
            disabled={tabLoading}
            className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
          >
            {tabLoading ? "Cargando..." : "Actualizar"}
          </button>
          <p className="text-xs text-slate-500">Muestra todos los feedId con imágenes de las últimas 24 horas</p>
        </div>
      );
    }

    return (
      <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 mb-8 flex flex-wrap items-end gap-4 animate-in fade-in slide-in-from-top-2">
        <div className="flex-1 min-w-[150px]">
          <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1">Fecha</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-medium outline-none"
          />
        </div>
        <div className="w-24">
          <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1">Hora</label>
          <input
            type="number"
            min="0" max="23"
            value={hour}
            onChange={(e) => setHour(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-medium outline-none"
          />
        </div>
        <div className="w-24">
          <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1">Min. Inicio</label>
          <input
            type="number"
            min="0" max="59"
            value={startMin}
            onChange={(e) => setStartMin(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-medium outline-none"
          />
        </div>
        <div className="w-24">
          <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1">Min. Fin</label>
          <input
            type="number"
            min="0" max="59"
            value={endMin}
            onChange={(e) => setEndMin(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-medium outline-none"
          />
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
        >
          {loading ? "Buscando..." : "Consultar"}
        </button>
      </div>
    );
  };

  const renderTodayContent = () => {
    if (tabLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo datos de hoy...</p>
        </div>
      );
    }

    if (tabErrors.today) {
      return (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">{tabErrors.today}</p>
        </div>
      );
    }

    if (!todayData || !Array.isArray(todayData) || todayData.length === 0) {
      return (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Activity className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">Sin datos disponibles para hoy</p>
        </div>
      );
    }

    const feedMap = new Map();
    todayData.forEach((item: any) => {
      const existing = feedMap.get(item.ftpUserId) || 0;
      feedMap.set(item.ftpUserId, existing + (item.totalImageCount || 0));
    });
    let aggregatedData = Array.from(feedMap, ([ftpUserId, totalImageCount]) => ({ ftpUserId, totalImageCount }));

    if (todaySortOrder) {
      aggregatedData.sort((a, b) =>
        todaySortOrder === 'asc'
          ? a.totalImageCount - b.totalImageCount
          : b.totalImageCount - a.totalImageCount
      );
    }

    const totalImages = Array.from(feedMap.values()).reduce((sum: number, count: number) => sum + count, 0);

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            icon={<ImageIcon size={24} />}
            title="Total Imágenes"
            value={totalImages.toLocaleString()}
            color="text-blue-600"
            bgColor="bg-blue-50"
          />
          <StatCard
            icon={<Users size={24} />}
            title="Feeds Activos"
            value={aggregatedData.length.toString()}
            color="text-purple-600"
            bgColor="bg-purple-50"
          />
          <StatCard
            icon={<Activity size={24} />}
            title="Promedio por Feed"
            value={Math.round(totalImages / aggregatedData.length).toLocaleString()}
            color="text-emerald-600"
            bgColor="bg-emerald-50"
          />
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <h3 className="text-lg font-bold text-slate-800 mb-6">Listado de ftpUsers - Hoy</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-bold text-slate-700">Feed ID</th>
                  <th
                    onClick={() => {
                      if (todaySortOrder === 'asc') {
                        setTodaySortOrder('desc');
                      } else if (todaySortOrder === 'desc') {
                        setTodaySortOrder(null);
                      } else {
                        setTodaySortOrder('asc');
                      }
                    }}
                    className="text-left py-3 px-4 font-bold text-slate-700 cursor-pointer hover:bg-slate-100 rounded transition-colors flex items-center gap-2"
                  >
                    Imágenes
                    {todaySortOrder === 'asc' && <ChevronUp size={16} />}
                    {todaySortOrder === 'desc' && <ChevronDown size={16} />}
                  </th>
                </tr>
              </thead>
              <tbody>
                {aggregatedData.map((entry: any, index: number) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-slate-700 font-mono text-xs">{entry.ftpUserId}</td>
                    <td className="py-3 px-4 text-slate-700 font-semibold">{(entry.totalImageCount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">Total de {aggregatedData.length} feeds</p>
        </div>
      </div>
    );
  };

  const renderByDateContent = () => {
    if (tabLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo datos para {selectedDate}...</p>
        </div>
      );
    }

    if (tabErrors.byDate) {
      return (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">{tabErrors.byDate}</p>
        </div>
      );
    }

    if (!byDateData || !Array.isArray(byDateData) || byDateData.length === 0) {
      return (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Calendar className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">Sin datos para la fecha {selectedDate}</p>
        </div>
      );
    }

    const feedMap = new Map();
    byDateData.forEach((item: any) => {
      const existing = feedMap.get(item.ftpUserId) || 0;
      feedMap.set(item.ftpUserId, existing + (item.totalImageCount || 0));
    });
    let aggregatedData = Array.from(feedMap, ([ftpUserId, totalImageCount]) => ({ ftpUserId, totalImageCount }));

    if (byDateSortOrder) {
      aggregatedData.sort((a, b) =>
        byDateSortOrder === 'asc'
          ? a.totalImageCount - b.totalImageCount
          : b.totalImageCount - a.totalImageCount
      );
    }

    const totalImages = Array.from(feedMap.values()).reduce((sum: number, count: number) => sum + count, 0);

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            icon={<ImageIcon size={24} />}
            title="Total Imágenes"
            value={totalImages.toLocaleString()}
            color="text-indigo-600"
            bgColor="bg-indigo-50"
          />
          <StatCard
            icon={<Calendar size={24} />}
            title="Feeds Activos"
            value={aggregatedData.length.toString()}
            color="text-orange-600"
            bgColor="bg-orange-50"
          />
          <StatCard
            icon={<Activity size={24} />}
            title="Promedio por Feed"
            value={Math.round(totalImages / aggregatedData.length).toLocaleString()}
            color="text-pink-600"
            bgColor="bg-pink-50"
          />
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <h3 className="text-lg font-bold text-slate-800 mb-6">Listado de ftpUsers - {selectedDate}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-bold text-slate-700">Feed ID</th>
                  <th
                    onClick={() => {
                      if (byDateSortOrder === 'asc') {
                        setByDateSortOrder('desc');
                      } else if (byDateSortOrder === 'desc') {
                        setByDateSortOrder(null);
                      } else {
                        setByDateSortOrder('asc');
                      }
                    }}
                    className="text-left py-3 px-4 font-bold text-slate-700 cursor-pointer hover:bg-slate-100 rounded transition-colors flex items-center gap-2"
                  >
                    Imágenes
                    {byDateSortOrder === 'asc' && <ChevronUp size={16} />}
                    {byDateSortOrder === 'desc' && <ChevronDown size={16} />}
                  </th>
                </tr>
              </thead>
              <tbody>
                {aggregatedData.map((entry: any, index: number) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-slate-700 font-mono text-xs">{entry.ftpUserId}</td>
                    <td className="py-3 px-4 text-slate-700 font-semibold">{(entry.totalImageCount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">Total de {aggregatedData.length} feeds</p>
        </div>
      </div>
    );
  };

  const renderHistoryContent = () => {
    if (tabLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo histórico...</p>
        </div>
      );
    }

    if (tabErrors.history) {
      return (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">{tabErrors.history}</p>
        </div>
      );
    }

    if (!historyData || !selectedFtpUser) {
      return (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Clock className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">Selecciona un Feed ID para ver el histórico</p>
        </div>
      );
    }

    let flattenedData: any[] = [];
    if (Array.isArray(historyData)) {
      flattenedData = historyData;
    } else if (typeof historyData === 'object') {
      flattenedData = Object.values(historyData).flat();
    }

    if (flattenedData.length === 0) {
      return (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Clock className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">Sin datos históricos disponibles</p>
        </div>
      );
    }

    const userDataByDate: { [key: string]: number } = {};
    flattenedData
      .filter((entry: any) => entry.ftpUserId === selectedFtpUser)
      .forEach((entry: any) => {
        const date = entry.monitoringDate;
        userDataByDate[date] = (userDataByDate[date] || 0) + (entry.totalImageCount || 0);
      });

    const sortedDates = Object.keys(userDataByDate).sort().reverse();
    const totalImages = Object.values(userDataByDate).reduce((sum, count) => sum + count, 0);

    if (sortedDates.length === 0) {
      return (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Clock className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">Sin datos para {selectedFtpUser}</p>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <h3 className="text-lg font-bold text-slate-800 mb-6">Histórico - {selectedFtpUser}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-bold text-slate-700">Fecha</th>
                  <th className="text-left py-3 px-4 font-bold text-slate-700">Total Imágenes</th>
                </tr>
              </thead>
              <tbody>
                {sortedDates.map((date) => (
                  <tr key={date} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-slate-700 font-semibold">{date}</td>
                    <td className="py-3 px-4 text-slate-700 font-semibold">
                      {userDataByDate[date].toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Total de {sortedDates.length} días | Total de imágenes: {totalImages.toLocaleString()}
          </p>
        </div>
      </div>
    );
  };

  const renderAssetsContent = () => {
    if (tabLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Cargando activos...</p>
          <p className="text-xs text-gray-400 italic">Esto puede tomar hasta 1 minuto</p>
        </div>
      );
    }

    if (tabErrors.assets) {
      return (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">{tabErrors.assets}</p>
        </div>
      );
    }

    if (!assetsData) {
      return (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Activity className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">Sin datos de activos disponibles</p>
        </div>
      );
    }

    const assets = Array.isArray(assetsData) ? assetsData : (assetsData.userStatuses || assetsData.assets || []);
    const activeAssets = assets.filter((item: any) => item.hasRecords === true);
    const inactiveAssets = assets.filter((item: any) => item.hasRecords === false);
    const totalImages = assets.reduce((sum: number, item: any) => sum + (item.totalImages || 0), 0);

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={<Activity size={24} />}
            title="Total Feeds"
            value={assets.length.toLocaleString()}
            color="text-blue-600"
            bgColor="bg-blue-50"
          />
          <StatCard
            icon={<ImageIcon size={24} />}
            title="Total Imágenes"
            value={totalImages.toLocaleString()}
            color="text-indigo-600"
            bgColor="bg-indigo-50"
          />
          <StatCard
            icon={<ImageIcon size={24} />}
            title="Feeds Activos"
            value={activeAssets.length.toLocaleString()}
            color="text-green-600"
            bgColor="bg-green-50"
          />
          <StatCard
            icon={<AlertCircle size={24} />}
            title="Feeds Inactivos"
            value={inactiveAssets.length.toLocaleString()}
            color="text-red-600"
            bgColor="bg-red-50"
          />
        </div>

        {activeAssets.length > 0 && (
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
            <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              Feeds Activos ({activeAssets.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-bold text-slate-700">Feed ID</th>
                    <th className="text-right py-3 px-4 font-bold text-slate-700">Total Imágenes</th>
                    <th className="text-center py-3 px-4 font-bold text-slate-700">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {activeAssets.map((asset: any, index: number) => (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-slate-700 font-mono text-xs">{asset.ftpUserId}</td>
                      <td className="py-3 px-4 text-slate-700 font-semibold text-right">{(asset.totalImages || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                          ✓ Activo
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-4">Total de {activeAssets.length} feeds activos</p>
          </div>
        )}

        {inactiveAssets.length > 0 && (
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
            <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full"></span>
              Feeds Inactivos ({inactiveAssets.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-bold text-slate-700">Feed ID</th>
                    <th className="text-center py-3 px-4 font-bold text-slate-700">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveAssets.map((asset: any, index: number) => (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-slate-700 font-mono text-xs">{asset.ftpUserId}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                          ✗ Inactivo
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-4">Total de {inactiveAssets.length} feeds inactivos</p>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === 'today') {
      return renderTodayContent();
    }

    if (activeTab === 'byDate') {
      return renderByDateContent();
    }

    if (activeTab === 'history') {
      return renderHistoryContent();
    }

    if (activeTab === 'assets') {
      return renderAssetsContent();
    }

    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo datos de monitoreo...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold mb-4">{error}</p>
          <button onClick={fetchData} className="text-red-600 font-bold underline">Intentar de nuevo</button>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Filter className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">Configura los filtros para visualizar los datos</p>
        </div>
      );
    }

    if (activeTab === 'minutes') {
      if (loading) {
        return (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
            <p className="text-gray-500 font-medium">Obteniendo datos de monitoreo...</p>
          </div>
        );
      }

      if (error) {
        return (
          <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
            <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
            <p className="text-red-800 font-bold mb-4">{error}</p>
            <button onClick={fetchData} className="text-red-600 font-bold underline">Intentar de nuevo</button>
          </div>
        );
      }

      if (!data) {
        return (
          <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
            <Filter className="text-slate-300 mx-auto mb-4" size={48} />
            <p className="text-slate-400 font-bold italic">Configura los filtros para visualizar los datos</p>
          </div>
        );
      }

      return (
        <div className="space-y-8 animate-in zoom-in-95 duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <StatCard icon={<ImageIcon size={24} />} title="Imágenes en Rango" value={(data.totalImagesInMinuteRange ?? 0).toLocaleString()} color="text-orange-600" bgColor="bg-orange-50" />
            <StatCard icon={<Users size={24} />} title="Usuarios en Rango" value={data.distinctFtpUsersInMinuteRange?.toString() || '0'} color="text-purple-600" bgColor="bg-purple-50" />
          </div>

        {data.ftpUserIdsInMinuteRange && data.ftpUserIdsInMinuteRange.length > 0 && (
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Users className="text-blue-500" size={20} />
              Usuarios conectados en ese rango horario
            </h3>
            <div className="flex flex-wrap gap-3">
              {data.ftpUserIdsInMinuteRange.map(id => (
                <span key={id} className="px-4 py-2 bg-slate-50 text-slate-600 rounded-xl font-mono text-sm font-bold border border-slate-100 hover:bg-white hover:shadow-sm transition-all cursor-default">
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      );
    }
  };

  return (
    <div className="p-8 bg-slate-50 min-h-screen font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="text-3xl font-black text-slate-800 tracking-tight">Monitoring Dashboard</h1>
          <p className="text-slate-500 font-medium mt-1">Ingesta y flujo de imágenes en Amazon S3</p>
        </div>
      </div>

      {/* Tab Selector */}
      <div className="flex bg-white p-2 rounded-3xl border border-slate-100 w-fit gap-2 mb-8 shadow-sm overflow-x-auto">
        {[
          { id: 'today', label: 'Hoy', icon: Activity },
          { id: 'byDate', label: 'Por Fecha', icon: Calendar },
          { id: 'history', label: 'Histórico', icon: Clock },
          { id: 'minutes', label: 'Rango Minutos', icon: Filter },
          { id: 'assets', label: 'Activos', icon: ImageIcon },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as TabType)}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all
              ${activeTab === tab.id
                ? 'bg-slate-900 text-white shadow-lg'
                : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'}
            `}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters Form */}
      {renderFilters()}

      {/* Main Content Area */}
      {renderContent()}
    </div>
  );
};

const StatCard = ({ icon, title, value, color, bgColor }: any) => (
  <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 flex items-center gap-6 hover:shadow-md transition-all">
    <div className={`p-5 rounded-2xl ${bgColor} ${color}`}>
      {icon}
    </div>
    <div>
      <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">{title}</p>
      <p className="text-3xl font-black text-slate-800 mt-1">{value}</p>
    </div>
  </div>
);

export default Monitor;